// Coordinator — Availability & Block Manager
// Manage per-doctor weekly schedules and time blocks (leave/unavailable)

import { useState, useEffect } from 'react';
import { Clock, Calendar, Shield, Plus, Trash2, Save, User } from 'lucide-react';

interface Doctor {
    id: string;
    firstName: string;
    lastName: string;
    specialty: string | null;
    calendarColor: string;
}

interface AvailabilityDay {
    id?: string;
    dayOfWeek: number;
    isActive: boolean;
    startTime: string; // HH:MM
    endTime: string;
    slotDurationMinutes: number;
}

interface DoctorBlock {
    id: string;
    blockStart: string;
    blockEnd: string;
    reason: string | null;
    isFullDay: boolean;
}

import { API_BASE } from '../config/api';

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const SLOT_DURATIONS = [15, 20, 30, 45, 60];

function formatTime(isoTime: string): string {
    // Parse ISO time like "1970-01-01T08:00:00.000Z" → "08:00"
    try {
        const d = new Date(isoTime);
        return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    } catch {
        return '08:00';
    }
}

export function AvailabilityManagerPage() {
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
    const [schedule, setSchedule] = useState<AvailabilityDay[]>([]);
    const [blocks, setBlocks] = useState<DoctorBlock[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // New block form
    const [newBlockStart, setNewBlockStart] = useState('');
    const [newBlockEnd, setNewBlockEnd] = useState('');
    const [newBlockReason, setNewBlockReason] = useState('');
    const [newBlockFullDay, setNewBlockFullDay] = useState(false);

    // Load doctors
    useEffect(() => {
        fetch(`${API_BASE}/staff?role=doctor`)
            .then(r => r.json())
            .then(res => {
                const docs = res.data || [];
                setDoctors(docs);
                if (docs.length > 0) setSelectedDoctor(docs[0]);
            })
            .catch(() => { });
    }, []);

    // Load availability + blocks when doctor changes
    useEffect(() => {
        if (!selectedDoctor) return;
        loadDoctorData(selectedDoctor.id);
    }, [selectedDoctor]);

    async function loadDoctorData(doctorId: string) {
        setLoading(true);
        try {
            const [availRes, blocksRes] = await Promise.all([
                fetch(`${API_BASE}/doctors/${doctorId}/availability`).then(r => r.json()),
                fetch(`${API_BASE}/doctors/${doctorId}/blocks`).then(r => r.json()),
            ]);

            // Parse availability into our format
            const availData = (availRes.data || []) as Array<{
                id: string; dayOfWeek: number; isActive: boolean;
                startTime: string; endTime: string; slotDurationMinutes: number;
            }>;

            // Create 7-day schedule (fill missing days)
            const sched: AvailabilityDay[] = Array.from({ length: 7 }, (_, i) => {
                const existing = availData.find(a => a.dayOfWeek === i);
                return existing
                    ? { ...existing, startTime: formatTime(existing.startTime), endTime: formatTime(existing.endTime) }
                    : { dayOfWeek: i, isActive: false, startTime: '08:00', endTime: '17:00', slotDurationMinutes: 30 };
            });

            setSchedule(sched);
            setBlocks(blocksRes.data || []);
        } catch {
            setSchedule(Array.from({ length: 7 }, (_, i) => ({
                dayOfWeek: i, isActive: false, startTime: '08:00', endTime: '17:00', slotDurationMinutes: 30,
            })));
            setBlocks([]);
        } finally {
            setLoading(false);
        }
    }

    function updateDay(dayOfWeek: number, updates: Partial<AvailabilityDay>) {
        setSchedule(prev => prev.map(d => d.dayOfWeek === dayOfWeek ? { ...d, ...updates } : d));
    }

    async function saveSchedule() {
        if (!selectedDoctor) return;
        setSaving(true);
        setSaveMessage('');
        try {
            const res = await fetch(`${API_BASE}/doctors/${selectedDoctor.id}/availability`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ days: schedule }),
            });
            if (res.ok) {
                setSaveMessage('✅ Horario guardado exitosamente');
            } else {
                const err = await res.json();
                setSaveMessage(`❌ ${err.error}`);
            }
        } catch {
            setSaveMessage('❌ Error al guardar');
        } finally {
            setSaving(false);
            setTimeout(() => setSaveMessage(''), 3000);
        }
    }

    async function createBlock() {
        if (!selectedDoctor || !newBlockStart || !newBlockEnd) return;
        try {
            const res = await fetch(`${API_BASE}/doctors/${selectedDoctor.id}/blocks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    blockStart: newBlockStart,
                    blockEnd: newBlockEnd,
                    reason: newBlockReason || null,
                    isFullDay: newBlockFullDay,
                }),
            });
            if (res.ok) {
                setNewBlockStart('');
                setNewBlockEnd('');
                setNewBlockReason('');
                setNewBlockFullDay(false);
                loadDoctorData(selectedDoctor.id);
            }
        } catch { /* noop */ }
    }

    async function deleteBlock(blockId: string) {
        if (!selectedDoctor) return;
        try {
            await fetch(`${API_BASE}/doctors/${selectedDoctor.id}/blocks/${blockId}`, { method: 'DELETE' });
            setBlocks(prev => prev.filter(b => b.id !== blockId));
        } catch { /* noop */ }
    }

    return (
        <div className="fade-in">
            <h1 className="page-title">
                <Clock size={22} style={{ display: 'inline', marginRight: 8 }} />
                Gestión de Disponibilidad
            </h1>

            {/* Doctor Selector */}
            <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <User size={16} color="var(--text-3)" />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>Médico:</span>
                    {doctors.map(doc => (
                        <button key={doc.id} onClick={() => setSelectedDoctor(doc)}
                            style={{
                                padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                                fontSize: 13, fontWeight: selectedDoctor?.id === doc.id ? 700 : 400,
                                border: `2px solid ${selectedDoctor?.id === doc.id ? doc.calendarColor : 'var(--border)'}`,
                                background: selectedDoctor?.id === doc.id ? `${doc.calendarColor}15` : 'transparent',
                                color: selectedDoctor?.id === doc.id ? doc.calendarColor : 'var(--text-2)',
                                transition: 'all 0.2s',
                            }}>
                            {doc.firstName} {doc.lastName}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-3)' }}>
                    <p>Cargando disponibilidad...</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {/* Weekly Schedule */}
                    <div className="card" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Calendar size={16} /> Horario Semanal
                            </h3>
                            <button onClick={saveSchedule} disabled={saving}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '6px 14px', borderRadius: 6, border: 'none',
                                    background: 'var(--primary)', color: '#fff',
                                    fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
                                    opacity: saving ? 0.7 : 1,
                                }}>
                                <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                        {saveMessage && (
                            <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, background: saveMessage.startsWith('✅') ? '#ecfdf5' : '#fef2f2', color: saveMessage.startsWith('✅') ? '#059669' : '#dc2626' }}>
                                {saveMessage}
                            </div>
                        )}
                        <table className="table" style={{ fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th>Día</th>
                                    <th>Activo</th>
                                    <th>Inicio</th>
                                    <th>Fin</th>
                                    <th>Duración</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedule.map(day => (
                                    <tr key={day.dayOfWeek} style={{ opacity: day.isActive ? 1 : 0.5 }}>
                                        <td style={{ fontWeight: 600 }}>{DAY_NAMES[day.dayOfWeek]}</td>
                                        <td>
                                            <input type="checkbox" checked={day.isActive}
                                                onChange={e => updateDay(day.dayOfWeek, { isActive: e.target.checked })}
                                                style={{ cursor: 'pointer', width: 16, height: 16 }} />
                                        </td>
                                        <td>
                                            {day.isActive ? (
                                                <input type="time" value={day.startTime}
                                                    onChange={e => updateDay(day.dayOfWeek, { startTime: e.target.value })}
                                                    style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
                                            ) : '—'}
                                        </td>
                                        <td>
                                            {day.isActive ? (
                                                <input type="time" value={day.endTime}
                                                    onChange={e => updateDay(day.dayOfWeek, { endTime: e.target.value })}
                                                    style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
                                            ) : '—'}
                                        </td>
                                        <td>
                                            {day.isActive ? (
                                                <select value={day.slotDurationMinutes}
                                                    onChange={e => updateDay(day.dayOfWeek, { slotDurationMinutes: Number(e.target.value) })}
                                                    style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }}>
                                                    {SLOT_DURATIONS.map(d => (
                                                        <option key={d} value={d}>{d} min</option>
                                                    ))}
                                                </select>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Blocks / Leave */}
                    <div className="card" style={{ padding: 16 }}>
                        <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Shield size={16} /> Bloqueos / Permisos
                        </h3>

                        {/* Add Block Form */}
                        <div style={{ padding: 12, borderRadius: 8, border: '1px dashed var(--border)', marginBottom: 16 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Plus size={14} /> Nuevo bloqueo
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                <div>
                                    <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 2 }}>Inicio</label>
                                    <input type="datetime-local" value={newBlockStart} onChange={e => setNewBlockStart(e.target.value)}
                                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 2 }}>Fin</label>
                                    <input type="datetime-local" value={newBlockEnd} onChange={e => setNewBlockEnd(e.target.value)}
                                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12 }} />
                                </div>
                            </div>
                            <input type="text" placeholder="Motivo (ej: Vacaciones, Conferencia)" value={newBlockReason}
                                onChange={e => setNewBlockReason(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, marginBottom: 8 }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={newBlockFullDay} onChange={e => setNewBlockFullDay(e.target.checked)} />
                                    Día completo
                                </label>
                                <button onClick={createBlock} disabled={!newBlockStart || !newBlockEnd}
                                    style={{
                                        padding: '6px 14px', borderRadius: 6, border: 'none',
                                        background: newBlockStart && newBlockEnd ? 'var(--primary)' : '#ccc',
                                        color: '#fff', fontSize: 12, fontWeight: 600,
                                        cursor: newBlockStart && newBlockEnd ? 'pointer' : 'default',
                                    }}>
                                    Crear bloqueo
                                </button>
                            </div>
                        </div>

                        {/* Existing Blocks */}
                        {blocks.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)', fontSize: 13 }}>
                                No hay bloqueos registrados
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {blocks.map(b => {
                                    const start = new Date(b.blockStart);
                                    const end = new Date(b.blockEnd);
                                    const isPast = end < new Date();
                                    return (
                                        <div key={b.id} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '8px 12px', borderRadius: 6,
                                            background: isPast ? 'var(--bg-2)' : '#fef3c7',
                                            border: `1px solid ${isPast ? 'var(--border)' : '#fbbf24'}`,
                                            opacity: isPast ? 0.6 : 1,
                                            fontSize: 12,
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: 600 }}>
                                                    {start.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' })}
                                                    {' '}→{' '}
                                                    {end.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' })}
                                                </div>
                                                <div style={{ color: 'var(--text-3)' }}>{b.reason || 'Sin motivo'}</div>
                                            </div>
                                            {!isPast && (
                                                <button onClick={() => deleteBlock(b.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
